
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import StaffCreateUserForm from '@/components/staff/StaffCreateUserForm';
import { Button } from '@/components/ui/button';
import { RefreshCw, Users, UserPlus, BarChart2, Upload, BookOpen, Zap } from 'lucide-react';
import { useStaffUserManagement } from '@/hooks/auth/useStaffUserManagement';
import StaffUserTable from '@/components/staff/StaffUserTable';
import StudentCreationWizard from '@/components/wizard/StudentCreationWizard';
import StaffCourseAssignmentModal from '@/components/staff/StaffCourseAssignmentModal';
import StaffUserAnalytics from '@/components/staff/StaffUserAnalytics';
import StaffUserManagementStats from '@/components/staff/StaffUserManagementStats';
import StaffQuickActions from '@/components/staff/StaffQuickActions';
import StaffErrorBoundary from '@/components/staff/StaffErrorBoundary';
import EnhancedStaffAssignmentDashboard from '@/components/staff/EnhancedStaffAssignmentDashboard';
import BulkStudentUpload from '@/components/staff/BulkStudentUpload';
import { updateUserByStaff } from '@/hooks/auth/staffUserManagement';
import { toast } from '@/components/ui/sonner';

const StaffUserManagement = () => {
  const {
    users,
    loading,
    hasStaffAccess,
    loadUsers,
    refreshData,
    bulkAssignCourses
  } = useStaffUserManagement();

  const [selectedUserForAssignment, setSelectedUserForAssignment] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const [selectedUserForAnalytics, setSelectedUserForAnalytics] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const [activeTab, setActiveTab] = useState('overview');

  if (!hasStaffAccess) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-muted-foreground">You don't have staff permissions to access this page.</p>
        </div>
      </div>
    );
  }

  const handleAssignCourse = (userId: string, userName: string) => {
    setSelectedUserForAssignment({ id: userId, name: userName });
  };

  const handleViewAnalytics = (userId: string, userName: string) => {
    setSelectedUserForAnalytics({ id: userId, name: userName });
  };

  const handleUserCreated = () => {
    refreshData();
    // Switch to overview tab to show the newly created user
    setActiveTab('overview');
    toast.success('User created successfully! Check the User Overview tab.');
  };

  const handleAssignmentComplete = () => {
    refreshData();
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'approve-pending':
        // Switch to overview tab to show pending users
        setActiveTab('overview');
        break;
      case 'create-user':
        setActiveTab('create');
        break;
      case 'bulk-assign':
        // This will be handled by the StaffQuickActions component
        break;
      default:
        break;
    }
  };

  const handleBulkApprove = async (userIds: string[]): Promise<boolean> => {
    try {
      let successCount = 0;
      for (const userId of userIds) {
        const success = await updateUserByStaff(userId, { is_approved: true });
        if (success) successCount++;
      }
      
      if (successCount > 0) {
        refreshData();
        toast.success(`Successfully approved ${successCount} out of ${userIds.length} users`);
        return true;
      } else {
        toast.error('Failed to approve users');
        return false;
      }
    } catch (error) {
      console.error('Error in bulk approve:', error);
      toast.error('Failed to approve users');
      return false;
    }
  };

  const handleBulkAssign = async (userIds: string[], courseIds: string[]): Promise<boolean> => {
    try {
      const success = await bulkAssignCourses(userIds, courseIds);
      if (success) {
        refreshData();
      }
      return success;
    } catch (error) {
      console.error('Error in bulk assign:', error);
      toast.error('Failed to assign courses');
      return false;
    }
  };

  return (
    <StaffErrorBoundary>
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Staff User Management</h1>
            <p className="text-muted-foreground mt-2">
              Manage users, assign courses, and track progress
            </p>
          </div>
          <Button onClick={refreshData} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Statistics and Quick Actions */}
        <StaffUserManagementStats 
          users={users} 
          loading={loading}
          onQuickAction={handleQuickAction}
        />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mt-6">
          <TabsList className="mb-6 grid grid-cols-6">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              User Overview
            </TabsTrigger>
            <TabsTrigger value="create" className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Create User
            </TabsTrigger>
            <TabsTrigger value="bulk-upload" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Bulk Upload
            </TabsTrigger>
            <TabsTrigger value="assignment" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Assignment
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart2 className="h-4 w-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="quick-actions" className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Quick Actions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <StaffUserTable
              users={users}
              loading={loading}
              onRefresh={loadUsers}
              onAssignCourse={handleAssignCourse}
              onViewAnalytics={handleViewAnalytics}
            />
          </TabsContent>

          <TabsContent value="create" className="space-y-6">
            <Tabs defaultValue="quick" className="w-full">
              <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto mb-6">
                <TabsTrigger value="quick">Quick Create (No Courses)</TabsTrigger>
                <TabsTrigger value="wizard">Full Wizard (With Courses)</TabsTrigger>
              </TabsList>
              
              <TabsContent value="quick">
                <StaffCreateUserForm onUserCreated={handleUserCreated} />
              </TabsContent>
              
              <TabsContent value="wizard">
                <StudentCreationWizard 
                  onClose={() => setActiveTab('overview')}
                  onSuccess={handleUserCreated}
                />
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="bulk-upload" className="space-y-6">
            <BulkStudentUpload 
              onUploadComplete={() => {
                refreshData();
                setActiveTab('overview');
              }}
            />
          </TabsContent>

          <TabsContent value="assignment" className="space-y-6">
            <EnhancedStaffAssignmentDashboard 
              users={users}
              onAssignmentComplete={handleAssignmentComplete}
            />
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <StaffUserAnalytics />
          </TabsContent>

          <TabsContent value="quick-actions" className="space-y-6">
            <StaffQuickActions
              users={users}
              onBulkApprove={handleBulkApprove}
              onBulkAssign={handleBulkAssign}
            />
          </TabsContent>
        </Tabs>

        {/* Course Assignment Modal */}
        {selectedUserForAssignment && (
          <StaffCourseAssignmentModal
            isOpen={true}
            onClose={() => setSelectedUserForAssignment(null)}
            userId={selectedUserForAssignment.id}
            userName={selectedUserForAssignment.name}
            onAssignmentComplete={handleAssignmentComplete}
          />
        )}
      </div>
    </StaffErrorBoundary>
  );
};

export default StaffUserManagement;
